"use client";

import { c as _c } from "react/compiler-runtime";
import { ChevronDown, ChevronsUpDown, ChevronUp } from "lucide-react";
import { Select as SelectPrimitive } from "radix-ui";
import type * as React from "react";
import { cn } from "~/lib/utils";
function Select(t0) {
  const $ = _c(4);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = <SelectPrimitive.Root data-slot="select" {...props} />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function SelectGroup(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("scroll-my-1", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectPrimitive.Group data-slot="select-group" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SelectValue(t0) {
  const $ = _c(4);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = <SelectPrimitive.Value data-slot="select-value" {...props} />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function SelectTrigger(t0) {
  const $ = _c(13);
  let children;
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      size: t1,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
  }
  const size = t1 === undefined ? "default" : t1;
  let t2;
  if ($[5] !== className) {
    t2 = cn("flex w-fit h-11 items-center justify-between gap-2 rounded-full border border-[var(--ink-20)] bg-white px-4 py-0 text-[14px] font-medium text-[var(--ink)] shadow-none transition-colors outline-none", "hover:border-[var(--blue)] focus:border-[var(--blue)] focus:ring-1 focus:ring-[var(--blue)]", "disabled:cursor-not-allowed disabled:opacity-50 data-[placeholder]:text-muted-foreground", "data-[size=sm]:h-8 data-[size=sm]:px-3 data-[size=sm]:text-xs", "*:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 *:data-[slot=select-value]:truncate", className);
    $[5] = className;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SelectPrimitive.Icon asChild={true}><ChevronsUpDown strokeWidth={2} className="pointer-events-none size-4 shrink-0 text-secondary" /></SelectPrimitive.Icon>;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== children || $[9] !== props || $[10] !== size || $[11] !== t2) {
    t4 = <SelectPrimitive.Trigger data-slot="select-trigger" data-size={size} className={t2} {...props}>{children}{t3}</SelectPrimitive.Trigger>;
    $[8] = children;
    $[9] = props;
    $[10] = size;
    $[11] = t2;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
function SelectContent(t0) {
  const $ = _c(22);
  let children;
  let className;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className,
      children,
      position: t1,
      align: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const position = t1 === undefined ? "item-aligned" : t1;
  const align = t2 === undefined ? "center" : t2;
  const t3 = position === "item-aligned";
  const t4 = position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1";
  let t5;
  if ($[6] !== className || $[7] !== t4) {
    t5 = cn("relative z-[10000] max-h-84 min-w-[12rem] origin-[var(--radix-select-content-transform-origin)] overflow-hidden rounded-[16px] border border-[var(--ink-20)] bg-white p-0 shadow-[0_8px_30px_rgb(0,0,0,0.12)] data-[state=open]:animate-fade-up", t4, className);
    $[6] = className;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <SelectScrollUpButton />;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = cn("p-0", "data-[position=popper]:h-[var(--radix-select-trigger-height)] data-[position=popper]:w-full data-[position=popper]:min-w-[var(--radix-select-trigger-width)]");
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== children || $[12] !== position) {
    t8 = <SelectPrimitive.Viewport data-position={position} className={t7}>{children}</SelectPrimitive.Viewport>;
    $[11] = children;
    $[12] = position;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <SelectScrollDownButton />;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== align || $[16] !== position || $[17] !== props || $[18] !== t3 || $[19] !== t5 || $[20] !== t8) {
    t10 = <SelectPrimitive.Portal><SelectPrimitive.Content data-slot="select-content" data-align-trigger={t3} className={t5} position={position} align={align} {...props}>{t6}{t8}{t9}</SelectPrimitive.Content></SelectPrimitive.Portal>;
    $[15] = align;
    $[16] = position;
    $[17] = props;
    $[18] = t3;
    $[19] = t5;
    $[20] = t8;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  return t10;
}
function SelectLabel(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("px-3 py-2 text-xs font-semibold text-secondary uppercase tracking-wider", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectPrimitive.Label data-slot="select-label" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SelectItem(t0) {
  const $ = _c(12);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("relative flex h-[44px] w-full cursor-pointer items-center rounded-none px-4 text-[14px] font-medium text-[var(--ink)] outline-none select-none transition-colors", "focus:bg-[#F0F5FF] focus:text-[var(--ink)]", "data-[state=checked]:bg-[var(--blue)] data-[state=checked]:text-white", "data-[disabled]:pointer-events-none data-[disabled]:opacity-50", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children) {
    t2 = <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>;
    $[6] = children;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== props || $[9] !== t1 || $[10] !== t2) {
    t3 = <SelectPrimitive.Item data-slot="select-item" className={t1} {...props}>{t2}</SelectPrimitive.Item>;
    $[8] = props;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}
function SelectSeparator(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("-mx-1 my-1 h-px bg-border", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectPrimitive.Separator data-slot="select-separator" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SelectScrollUpButton(t0) {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("z-10 flex cursor-default items-center justify-center bg-surface py-1 [&_svg]:size-4", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ChevronUp strokeWidth={2} />;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props || $[7] !== t1) {
    t3 = <SelectPrimitive.ScrollUpButton data-slot="select-scroll-up-button" className={t1} {...props}>{t2}</SelectPrimitive.ScrollUpButton>;
    $[6] = props;
    $[7] = t1;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
function SelectScrollDownButton(t0) {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("z-10 flex cursor-default items-center justify-center bg-surface py-1 [&_svg]:size-4", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ChevronDown strokeWidth={2} />;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props || $[7] !== t1) {
    t3 = <SelectPrimitive.ScrollDownButton data-slot="select-scroll-down-button" className={t1} {...props}>{t2}</SelectPrimitive.ScrollDownButton>;
    $[6] = props;
    $[7] = t1;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
export { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectScrollDownButton, SelectScrollUpButton, SelectSeparator, SelectTrigger, SelectValue };